import {useCallback, useEffect, useMemo} from 'react';
import {useLocation, useParams} from 'react-router-dom';

import {userApi} from 'api';
import {Loader} from 'components';
import {AuthSource} from 'enums';
import {useApiData} from 'hooks';
import {RETURN_URL_QUERY} from 'utils/user-auth';

import {JoinSessionError} from '../JoinSessionError';

export const JoinSessionIntegration: React.FC = () => {
    const location = useLocation();
    const {sessionId} = useParams<{sessionId: string}>();
    const {source, token, deviceId} = useMemo(() => {
        const searchParams = new URLSearchParams(location.search);
        return {
            source: searchParams.get('source') as AuthSource,
            token: searchParams.get('token'),
            deviceId: searchParams.get('deviceId'),
        };
    }, [location]);

    const hasRequiredParams = useMemo(() => !!source && !!token && !!deviceId, [source, token, deviceId]);

    const onReceivedOneTimeToken = useCallback((response: {token: string}) => {
        const oneTimeToken = response.token;

        const returnUrl = `/session/join/agent/${sessionId}`;
        const query = `source=${source}&token=${oneTimeToken}&${RETURN_URL_QUERY}=${returnUrl}`;
        window.location.href = `${window.location.origin}/authorize?${query}`;
    }, [source, sessionId]);

    const {send: getOneTimeToken, loading, error} = useApiData(userApi.getOneTimeToken, {onSuccess: onReceivedOneTimeToken});

    useEffect(() => {
        if (hasRequiredParams) {
            getOneTimeToken(source, {token, deviceId});
        }
    }, [source, token, deviceId, hasRequiredParams, getOneTimeToken]);

    if (loading) {
        return <Loader svgClassName='tw-max-h-16' />;
    } else if (error || !hasRequiredParams) {
        return <JoinSessionError error={error} notAuthorized={!!error || !hasRequiredParams} />;
    }

    return null;
};
